import React, { useEffect, useMemo, useState } from "react";
import { Image as ImageIcon, Trash2, Eye, EyeOff } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";

const CATEGORIES = ["lifted", "supplies", "affiliate", "mascot", "watermark", "product", "promo", "og_template"];

export default function AdminBrandAssets() {
  const [assets, setAssets] = useState([]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [filterCat, setFilterCat] = useState("");
  const [form, setForm] = useState({
    category: "lifted", label: "", url: "", public: false,
    tags: "", mime: "", width: "", height: "",
    watermark_position: "", watermark_opacity: "", watermark_margin_px: "",
    notes: "",
  });

  const reload = async () => {
    try {
      const { data } = await api.get("/admin/brand-assets", { params: filterCat ? { category: filterCat } : {} });
      setAssets(data);
    } catch (e) { setErr(apiErrorMessage(e)); }
  };
  useEffect(() => { reload(); }, [filterCat]);  // eslint-disable-line react-hooks/exhaustive-deps

  const grouped = useMemo(() => {
    const g = {};
    for (const a of assets) (g[a.category] = g[a.category] || []).push(a);
    return g;
  }, [assets]);

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = {
        category: form.category, label: form.label, url: form.url,
        public: !!form.public,
        tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (form.mime) payload.mime = form.mime;
      if (form.width) payload.width = parseInt(form.width);
      if (form.height) payload.height = parseInt(form.height);
      if (form.notes) payload.notes = form.notes;
      const wm = {};
      if (form.watermark_position) wm.position = form.watermark_position;
      if (form.watermark_opacity)  wm.opacity  = parseFloat(form.watermark_opacity);
      if (form.watermark_margin_px) wm.margin_px = parseInt(form.watermark_margin_px);
      if (Object.keys(wm).length) payload.watermark_meta = wm;
      await api.post("/admin/brand-assets", payload);
      setForm({ ...form, label: "", url: "", tags: "", mime: "", width: "", height: "",
                watermark_position: "", watermark_opacity: "", watermark_margin_px: "", notes: "" });
      await reload();
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setBusy(false); }
  };

  const togglePublic = async (a) => {
    await api.put(`/admin/brand-assets/${a.id}`, { public: !a.public });
    await reload();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this asset?")) return;
    await api.delete(`/admin/brand-assets/${id}`);
    await reload();
  };

  return (
    <div data-testid="admin-brand-assets" className="space-y-6">
      <header className="fade-up">
        <div className="eyebrow flex items-center gap-2"><ImageIcon className="h-3 w-3" /> Brand</div>
        <h1 className="headline-xl">Brand asset library</h1>
        <p className="text-lifted-dim text-sm mt-2 max-w-2xl">
          Shell for Phase 9 — uploads register URLs only (S3 wiring deferred to Phase 10). Watermark metadata is captured for Phase 11 dynamic OG image generation.
        </p>
      </header>

      {err && <div className="text-rose-300 text-sm" data-testid="brand-assets-error">{err}</div>}

      {/* Filter */}
      <div className="flex gap-2 flex-wrap items-center" data-testid="brand-assets-filters">
        <span className="text-xs text-lifted-mute font-bebas tracking-[0.18em] uppercase">Category:</span>
        <button onClick={() => setFilterCat("")} className={`chip cursor-pointer ${filterCat === "" ? "chip--green" : ""}`}
                data-testid="brand-assets-filter-all">all ({assets.length})</button>
        {CATEGORIES.map((c) => (
          <button key={c} onClick={() => setFilterCat(c)}
                  data-testid={`brand-assets-filter-${c}`}
                  className={`chip cursor-pointer ${filterCat === c ? "chip--green" : ""}`}>{c} ({grouped[c]?.length || 0})</button>
        ))}
      </div>

      {/* Upload form */}
      <form onSubmit={submit} className="glass-panel p-5 space-y-3" data-testid="brand-assets-form">
        <div className="eyebrow !text-lifted-text-dim">Register new asset</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input-lifted text-sm" data-testid="brand-assets-form-category">
            {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Label *" className="input-lifted text-sm" data-testid="brand-assets-form-label" required />
          <input value={form.url}   onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="https://… *" className="input-lifted text-sm md:col-span-2" data-testid="brand-assets-form-url" required />
          <input value={form.tags}  onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="tags, comma, separated" className="input-lifted text-sm md:col-span-2" data-testid="brand-assets-form-tags" />
          <input value={form.mime}  onChange={(e) => setForm({ ...form, mime: e.target.value })} placeholder="mime (image/svg+xml)" className="input-lifted text-sm" />
          <label className="flex items-center gap-2 text-xs text-lifted-dim">
            <input type="checkbox" checked={form.public} onChange={(e) => setForm({ ...form, public: e.target.checked })} data-testid="brand-assets-form-public" />
            Public
          </label>
          <input value={form.width}  onChange={(e) => setForm({ ...form, width: e.target.value })} placeholder="width px" className="input-lifted text-sm" type="number" />
          <input value={form.height} onChange={(e) => setForm({ ...form, height: e.target.value })} placeholder="height px" className="input-lifted text-sm" type="number" />
        </div>
        <details className="text-xs text-lifted-dim">
          <summary className="cursor-pointer text-lifted-mute">Watermark metadata (optional, Phase 11)</summary>
          <div className="grid grid-cols-3 gap-2 mt-2">
            <input value={form.watermark_position}   onChange={(e) => setForm({ ...form, watermark_position: e.target.value })} placeholder="position (bottom-right)" className="input-lifted text-sm" />
            <input value={form.watermark_opacity}    onChange={(e) => setForm({ ...form, watermark_opacity: e.target.value })} placeholder="opacity 0-1" className="input-lifted text-sm" type="number" step="0.05" />
            <input value={form.watermark_margin_px}  onChange={(e) => setForm({ ...form, watermark_margin_px: e.target.value })} placeholder="margin px" className="input-lifted text-sm" type="number" />
          </div>
        </details>
        <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes" className="input-lifted text-sm w-full" rows={2} />
        <button disabled={busy || !form.label || !form.url} className="btn-lifted" type="submit" data-testid="brand-assets-form-submit">{busy ? "Saving…" : "Register asset"}</button>
      </form>

      {/* Listing */}
      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="brand-assets-list">
        {assets.map((a) => (
          <article key={a.id} data-testid={`brand-asset-${a.id}`} className="glass-panel p-4 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-anton text-base uppercase text-lifted-text">{a.label}</div>
                <div className="text-xs text-lifted-mute"><span className="chip">{a.category}</span> {a.public ? <span className="chip chip--green">public</span> : <span className="chip">private</span>}</div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => togglePublic(a)} className="btn-ghost !p-1.5 !text-[0.6rem]" title={a.public ? "Make private" : "Make public"} data-testid={`brand-asset-toggle-${a.id}`}>
                  {a.public ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                </button>
                <button onClick={() => remove(a.id)} className="btn-ghost !p-1.5 !text-[0.6rem] hover:!text-rose-300" data-testid={`brand-asset-delete-${a.id}`}>
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </div>
            <div className="text-xs text-lifted-dim break-all">{a.url}</div>
            {a.tags?.length > 0 && <div className="flex gap-1 flex-wrap">{a.tags.map((t) => <span key={t} className="chip chip--purple text-[0.6rem]">{t}</span>)}</div>}
            {a.watermark_meta && Object.keys(a.watermark_meta).length > 0 && (
              <div className="text-[0.65rem] text-lifted-dim border-t border-white/5 pt-2">watermark: {Object.entries(a.watermark_meta).filter(([,v]) => v != null).map(([k,v]) => `${k}=${v}`).join(" · ")}</div>
            )}
          </article>
        ))}
        {assets.length === 0 && <div className="md:col-span-3 text-center text-lifted-mute py-8">No brand assets registered yet.</div>}
      </section>
    </div>
  );
}
